import React, { useState, useEffect } from 'react';
import { FiCheckCircle, FiUserPlus, FiArrowRight, FiLoader } from 'react-icons/fi';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const CallSummary = ({ isGuest, currentBalance }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [finalData, setFinalData] = useState(null);
    const [loading, setLoading] = useState(true);

    const initialData = location.state || { duration: '0s', cost: 0, balance: currentBalance || 0 };

    useEffect(() => {
        const fetchFinalStatus = async () => {
            try {
                // Wait 2-3 seconds for Twilio webhook to hit backend and update cost
                await new Promise(r => setTimeout(r, 2500));

                const token = localStorage.getItem('token');
                const sessionId = localStorage.getItem('sessionId');
                const headers = {};
                if (token) headers.Authorization = `Bearer ${token}`;
                if (sessionId) headers['x-session-id'] = sessionId;

                const res = await axios.get(`${API_URL}/calls/latest`, { headers });

                const durMin = Math.ceil((res.data.duration || 0) / 60);
                setFinalData({
                    duration: `${res.data.duration || 0}s`,
                    cost: res.data.cost || 0,
                    // We also fetch current balance to show actual remaining
                    balanceNow: res.data.user_balance_at_end || currentBalance
                });
            } catch (err) {
                console.error('Failed to fetch final call status', err);
                setFinalData(initialData);
            } finally {
                setLoading(false);
            }
        };

        fetchFinalStatus();
    }, []);

    const data = finalData || initialData;

    return (
        <div className="card animate-fade" style={{ maxWidth: '500px', margin: '4rem auto', textAlign: 'center' }}>
            <div style={{ color: 'var(--success)', marginBottom: '1rem' }}>
                <FiCheckCircle size={64} />
            </div>
            <h1>Call Ended</h1>
            <p>Secure connection closed successfully.</p>

            <div style={{ display: 'flex', justifyContent: 'space-around', margin: '2.5rem 0', background: '#0f172a', padding: '1.5rem', borderRadius: '1rem', position: 'relative' }}>
                {loading && (
                    <div style={{ position: 'absolute', inset: 0, background: 'rgba(15,23,42,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '1rem', zIndex: 1, gap: '10px' }}>
                        <FiLoader className="spin" /> <span style={{ fontSize: '0.875rem' }}>Confirming final cost...</span>
                    </div>
                )}
                <div>
                    <p style={{ fontSize: '0.75rem' }}>Duration</p>
                    <p style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--text-main)' }}>{data.duration}</p>
                </div>
                <div>
                    <p style={{ fontSize: '0.75rem' }}>Minutes Charged</p>
                    <p style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--text-main)' }}>
                        {data.minutes_charged || Math.ceil((parseInt(data.duration) || 0) / 60)} min
                    </p>
                </div>
                <div>
                    <p style={{ fontSize: '0.75rem' }}>Minutes Remaining</p>
                    <p style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--text-main)' }}>
                        {Math.floor(data.balanceNow ?? currentBalance)} min
                    </p>
                </div>
            </div>

            {isGuest && currentBalance > 0 && (
                <div className="card" style={{ background: 'rgba(99, 102, 241, 0.1)', border: '1px solid var(--primary)', padding: '1.5rem' }}>
                    <h3 style={{ color: 'var(--primary)', marginBottom: '0.5rem' }}>Save your balance!</h3>
                    <p style={{ marginBottom: '1.5rem' }}>Your ${currentBalance.toFixed(2)} balance will expire unless you sign in.</p>

                    <button
                        className="primary"
                        style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
                        onClick={() => navigate('/login')}
                    >
                        <FiUserPlus /> Sign In to Save Balance <FiArrowRight />
                    </button>
                </div>
            )}

            <button
                style={{ background: 'none', border: 'none', color: 'var(--text-muted)', marginTop: '1.5rem', cursor: 'pointer', fontWeight: 500 }}
                onClick={() => navigate('/call')}
            >
                Make Another Call
            </button>
        </div>
    );
};

export default CallSummary;
